import React, { useState, useEffect, useRef } from 'react';
import {
  FaUser,
  FaHandshake,
  FaChartLine,
  FaMedal,
  FaBriefcase,
  FaPaperPlane,
  FaExclamationCircle,
  FaCheckCircle,
  FaArrowRight,
  FaUsers,
  FaUserGraduate,
  FaFilePdf
} from "react-icons/fa";
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './CareerPage.css';

// Google Sheets URL
// const GOOGLE_SHEETS_URL = 'https://script.google.com/macros/s/AKfycbwDXG5EUFRQYFcUo_7Zl3K_uitoc57gJcIEhaNPpb9KDVjDGTFbSNeMnYrCWIhUhaPe/exec';
const GOOGLE_SHEETS_URL = 'https://script.google.com/macros/s/AKfycbxJx99FRIBAvkF_MZxONTBECGIXDBu1cvDyvz03lg7RCsikvm2oDrCPBCSkdSlD8MvW/exec';

const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const base64Data = reader.result.split(',')[1];
      resolve(base64Data);
    };
    reader.onerror = (error) => reject(error);
  });
};

const CareerPage = () => {
  const [resume, setResume] = useState(null);
  const [resetKey, setResetKey] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    contact: '',
    yearOfGraduation: '',
    gender: '',
    experienceInYears: '',
    currentEmployer: '',
    currentCTC: '',
    expectedCTC: '',
    noticePeriod: '',
    skillSet: '',
    source: '',
    currentLocation: '',
    preferredLocation: '',
  });

  // Warn the user before closing the tab or navigating away while upload is in progress
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (loading) {
        e.preventDefault();
        e.returnValue = 'Upload is in progress. Are you sure you want to leave?';
        return 'Upload is in progress. Are you sure you want to leave?';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [loading]);

  const handleResumeUpload = (e) => {
    const uploadedFile = e.target.files[0];
    if (uploadedFile) {
      const validExtensions = /pdf|doc|docx/i;
      if (!validExtensions.test(uploadedFile.name)) {
        setError('Invalid file type. Please upload a PDF or Word document.');
        setResume(null);
      } else {
        setResume(uploadedFile);
        setError('');
      }
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const resetForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      contact: '',
      yearOfGraduation: '',
      gender: '',
      experienceInYears: '',
      currentEmployer: '',
      currentCTC: '',
      expectedCTC: '',
      noticePeriod: '',
      skillSet: '',
      source: '',
      currentLocation: '',
      preferredLocation: '',
    });
    setResume(null);
    setResetKey((prev) => prev + 1);
    setSubmitted(false);
    setError('');
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSubmitted(false);
    setError('');
    setUploadProgress(0);

    if (!resume) {
      setError('Please upload your resume.');
      setLoading(false);
      return;
    }

    // 1. Submit to Google Sheets (Drive upload) in background
    if (GOOGLE_SHEETS_URL) {
      fileToBase64(resume)
        .then((base64File) => {
          fetch(GOOGLE_SHEETS_URL, {
            method: 'POST',
            mode: 'no-cors',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              ...formData,
              fileData: base64File,
              fileName: resume.name,
              fileMimeType: resume.type,
            }),
          })
            .then(() => {
              console.log('Saved to Careers Google Sheet and uploaded resume successfully!');
            })
            .catch((error) => {
              console.error('Google Sheets saving error (Careers):', error);
            });
        })
        .catch((err) => {
          console.error('Error converting resume to base64:', err);
        });
    }

    // 2. Submit to backend API
    const formDataToSend = new FormData();
    for (const key in formData) {
      formDataToSend.append(key, formData[key]);
    }
    formDataToSend.append('pdf', resume);

    try {
      const response = await axios.post('https://kesvaan-6.onrender.com/apply', formDataToSend, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percentCompleted);
          } else {
            setUploadProgress(50);
          }
        }
      });
      resetForm();
      setSubmitted(true);
      setIsModalOpen(true);
    } catch (error) {
      console.error('Failed to send data to backend:', error);

      if (GOOGLE_SHEETS_URL) {
        resetForm();
        setSubmitted(true);
        setIsModalOpen(true);
      } else {
        setError('There was an error submitting your application. Please try again.');
        toast.error(
          <span>
            <FaExclamationCircle /> {error.message}
          </span>
        );
      }
    } finally {
      setLoading(false);
    }
  };

  // Easter Egg: Click 'Mentorship' -> 'Flexible Hours' -> 'Learning Programs' to unlock retro mini-game
  const [careerClicks, setCareerClicks] = useState([]);
  const [showGame, setShowGame] = useState(false);

  const handleCareerCardClick = (cardName) => {
    setCareerClicks(prev => {
      const next = [...prev, cardName];
      const trimmed = next.slice(-3);
      if (trimmed.join(',') === 'Mentorship,Flexible Hours,Learning Programs') {
        setShowGame(true);
        return [];
      }
      return trimmed;
    });
  };

  return (
    <div className="career-page-container career-root">
      {showGame && <RetroGame onClose={() => setShowGame(false)} />}
      <ToastContainer />


      {/* ── Header ── */}
      <header className="career-header">
        <div className="header-tagline-badge">
          <span className="live-dot" /> We Are Hiring
        </div>
        <h1>Join Our Team</h1>
        <p>Discover exciting career opportunities and shape the future of IT & digital automation with us.</p>
      </header>

      {/* ── Culture ── */}
      <section className="career-section culture">
        <div className="section-label">Life At iMatrix</div>
        <h2>Our Company Culture</h2>
        <p className="culture-desc">We value innovation, collaboration, and constant self-development. Join a supportive workplace that inspires everyday excellence.</p>

        <div className="culture-images">
          <div className="culture-img-card">
            <img src="/images/effective-team-collaboration-min.jpg" alt="Team Collaboration" />
            <div className="img-caption">Team Collaboration</div>
          </div>
          <div className="culture-img-card">
            <img src="/images/community-engagement-1.avif" alt="Community Engagement" />
            <div className="img-caption">Community Engagement</div>
          </div>
          <div className="culture-img-card">
            <img src="/images/0922-benefits-of-offering-employee-training-development-Inline1.webp" alt="Employee Development" />
            <div className="img-caption">Professional Development</div>
          </div>
        </div>
      </section>

      {/* ── Benefits & Growth ── */}
      <section className="career-section development">
        <div className="section-label">Perks & Growth</div>
        <h2>Growth & Benefits</h2>
        <p className="benefits-desc">We invest in our employees' health, skills, and future with standard support and learning models.</p>

        <div className="benefits-grid-wrap">
          <div
            className="benefit-card"
            onClick={() => handleCareerCardClick('Learning Programs')}
            style={{ cursor: 'pointer' }}
          >
            <FaChartLine className="benefit-icon" />
            <h3>Learning Programs</h3>
            <p>Direct sponsorship for certifications and skill courses.</p>
          </div>
          <div
            className="benefit-card"
            onClick={() => handleCareerCardClick('Mentorship')}
            style={{ cursor: 'pointer' }}
          >
            <FaHandshake className="benefit-icon" />
            <h3>Mentorship</h3>
            <p>One-on-one sessions with senior engineers and architects.</p>
          </div>
          <div className="benefit-card">
            <FaPaperPlane className="benefit-icon" />
            <h3>Career Pathways</h3>
            <p>Clear milestones and annual structural reviews.</p>
          </div>
          <div className="benefit-card">
            <FaMedal className="benefit-icon" />
            <h3>Health Programs</h3>
            <p>Comprehensive health support policies and annual checks.</p>
          </div>
          <div
            className="benefit-card"
            onClick={() => handleCareerCardClick('Flexible Hours')}
            style={{ cursor: 'pointer' }}
          >
            <FaBriefcase className="benefit-icon" />
            <h3>Flexible Hours</h3>
            <p>Balanced schedules that support continuous work-life balance.</p>
          </div>
          <div className="benefit-card">
            <FaUser className="benefit-icon" />
            <h3>Paid Time Off</h3>
            <p>Robust vacation allowances and national day leaves.</p>
          </div>
        </div>
      </section>

      {/* ── Why Work With Us ── */}
      <section className="career-section why-work-with-us">
        <div className="section-label">Our Philosophy</div>
        <h2>Why Work With Us</h2>
        <p className="why-desc">
          At iMatrix Technologies, we foster a positive, supportive, and inclusive workspace where every voice matters.
        </p>

        <div className="philosophy-grid">
          <div className="philosophy-card">
            <FaCheckCircle className="check-icon" />
            <div>
              <h4>Collaborative Environment</h4>
              <p>We actively encourage inter-departmental teamwork and open feedback networks.</p>
            </div>
          </div>
          <div className="philosophy-card">
            <FaCheckCircle className="check-icon" />
            <div>
              <h4>Continuous Advancement</h4>
              <p>Work alongside experts using modern cloud stacks, APIs, and high-performance server grids.</p>
            </div>
          </div>
          <div className="philosophy-card">
            <FaCheckCircle className="check-icon" />
            <div>
              <h4>Balanced Schedules</h4>
              <p>Maintain your mental and physical health with supportive shift allocations.</p>
            </div>
          </div>
          <div className="philosophy-card">
            <FaCheckCircle className="check-icon" />
            <div>
              <h4>Social Purpose</h4>
              <p>Participate in regional community engagement initiatives and tech awareness workshops.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Form Section ── */}
      <section className="career-section apply">
        <div className="section-label">Online Application</div>
        <h2>Apply Now</h2>
        <p className="apply-desc">Fill out your profile details. Our talent acquisition specialists will review your application package immediately.</p>

        <form className="career-form" onSubmit={handleSubmit}>

          {/* Subheading 1: Personal Details */}
          <div className="form-subheading">
            <FaUser className="sub-icon" /> 1. Personal & Contact Information
          </div>

          <div className="form-group">
            <label>First Name *</label>
            <input type="text" name="firstName" value={formData.firstName} onChange={handleInputChange} required placeholder="John" />
          </div>
          <div className="form-group">
            <label>Last Name *</label>
            <input type="text" name="lastName" value={formData.lastName} onChange={handleInputChange} required placeholder="Doe" />
          </div>
          <div className="form-group">
            <label>Email Address *</label>
            <input type="email" name="email" value={formData.email} onChange={handleInputChange} required placeholder="john.doe@example.com" />
          </div>
          <div className="form-group">
            <label>Contact Number *</label>
            <input type="text" name="contact" value={formData.contact} onChange={handleInputChange} required placeholder="+91 XXXXX XXXXX" />
          </div>
          <div className="form-group">
            <label>Gender *</label>
            <select name="gender" value={formData.gender} onChange={handleInputChange} required>
              <option value="">Select Gender</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Other">Other</option>
            </select>
          </div>
          <div className="form-group">
            <label>Graduation Year *</label>
            <input type="text" name="yearOfGraduation" value={formData.yearOfGraduation} onChange={handleInputChange} required placeholder="e.g. 2024" />
          </div>

          {/* Subheading 2: Professional Details */}
          <div className="form-subheading">
            <FaBriefcase className="sub-icon" /> 2. Professional & Technical Experience
          </div>

          <div className="form-group">
            <label>Experience (Years) *</label>
            <input type="text" name="experienceInYears" value={formData.experienceInYears} onChange={handleInputChange} required placeholder="e.g. 3" />
          </div>
          <div className="form-group">
            <label>Current Employer</label>
            <input type="text" name="currentEmployer" value={formData.currentEmployer} onChange={handleInputChange} placeholder="e.g. Tech Solutions Corp" />
          </div>
          <div className="form-group">
            <label>Current CTC (LPA)</label>
            <input type="text" name="currentCTC" value={formData.currentCTC} onChange={handleInputChange} placeholder="e.g. 4.5 LPA" />
          </div>
          <div className="form-group">
            <label>Expected CTC (LPA) *</label>
            <input type="text" name="expectedCTC" value={formData.expectedCTC} onChange={handleInputChange} required placeholder="e.g. 6.5 LPA" />
          </div>
          <div className="form-group full-width">
            <label>Technical Skill Set *</label>
            <input type="text" name="skillSet" value={formData.skillSet} onChange={handleInputChange} required placeholder="e.g. React, TallyPrime, SQL, Network Routing, Customer Relations" />
          </div>

          {/* Subheading 3: Preferences & Resume */}
          <div className="form-subheading">
            <FaFilePdf className="sub-icon" /> 3. Preferences & Document Package
          </div>

          <div className="form-group">
            <label>Current Location *</label>
            <input type="text" name="currentLocation" value={formData.currentLocation} onChange={handleInputChange} required placeholder="e.g. Chennai" />
          </div>
          <div className="form-group">
            <label>Preferred Location *</label>
            <input type="text" name="preferredLocation" value={formData.preferredLocation} onChange={handleInputChange} required placeholder="e.g. Coimbatore" />
          </div>
          <div className="form-group full-width">
            <label className="file-upload-label">Upload Resume (PDF / DOC) *</label>
            <div className="file-upload-wrapper">
              <input type="file" key={resetKey} accept=".pdf,.doc,.docx" onChange={handleResumeUpload} required={!resume} />
              <span className="file-upload-text">
                {resume ? `Selected: ${resume.name}` : "Click to select or drag PDF/Word document here"}
              </span>
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="cancel-button" onClick={resetForm} disabled={loading}>Reset Form</button>
            <button type="submit" className="submit-button" disabled={loading}>
              {loading ? 'Submitting Application...' : 'Submit My Application'}
            </button>
          </div>
        </form>

        {/* ── Loading / Uploading Modal Overlay ── */}
        {loading && (
          <div className="upload-loading-overlay">
            <div className="upload-loading-card">
              <div className="upload-pulse-ring">
                <div className="pulse-circle"></div>
                <FaFilePdf className="upload-pulse-icon" />
              </div>
              <h2>Uploading Application</h2>
              <p className="upload-warning-text">
                Please hold on while we securely upload your resume and submit your profile details.
                <br />
                <strong>Do not close this tab or navigate away.</strong>
              </p>
              
              <div className="upload-progress-container-modal">
                <div className="upload-progress-text-modal">
                  <span>{uploadProgress === 100 ? 'Processing details...' : 'Transmitting files...'}</span>
                  <span>{uploadProgress}%</span>
                </div>
                <div className="upload-progress-track-modal">
                  <div
                    className="upload-progress-bar-modal"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="error-message">
            <FaExclamationCircle /> <span>{error}</span>
          </div>
        )}
      </section>

      {/* ── Success Modal ── */}
      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>
              <FaCheckCircle style={{ color: '#10b981' }} />
              Application Received!
            </h2>
            <p>Thank you for submitting your credentials to iMatrix Technologies. Our recruitment specialists will review your application package and contact you shortly.</p>
            <button className="close-btn" onClick={closeModal}>Return to Careers</button>
          </div>
        </div>
      )}

      <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} />
    </div>
  );
};

// ── Retro Snake Game Component ──
const RetroGame = ({ onClose }) => {
  const canvasRef = useRef(null);
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(() => parseInt(localStorage.getItem('snake_highscore') || '0', 10));
  const [gameOver, setGameOver] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const GRID_SIZE = 20;
    const TILE_COUNT = canvas.width / GRID_SIZE;

    let snake = [{ x: 10, y: 10 }];
    let food = { x: 5, y: 5 };
    let dx = 1;
    let dy = 0;
    let gameInterval;
    let currentScore = 0;

    const generateFood = () => {
      let rx, ry;
      while (true) {
        rx = Math.floor(Math.random() * TILE_COUNT);
        ry = Math.floor(Math.random() * TILE_COUNT);
        if (!snake.some(segment => segment.x === rx && segment.y === ry)) {
          break;
        }
      }
      food = { x: rx, y: ry };
    };

    const update = () => {
      const head = { x: snake[0].x + dx, y: snake[0].y + dy };

      // Wall collision or self collision
      if (
        head.x < 0 || head.x >= TILE_COUNT ||
        head.y < 0 || head.y >= TILE_COUNT ||
        snake.some(segment => segment.x === head.x && segment.y === head.y)
      ) {
        setGameOver(true);
        clearInterval(gameInterval);
        return;
      }

      snake.unshift(head);

      // Food collection
      if (head.x === food.x && head.y === food.y) {
        currentScore += 10;
        setScore(currentScore);
        if (currentScore > highScore) {
          setHighScore(currentScore);
          localStorage.setItem('snake_highscore', currentScore.toString());
        }
        generateFood();
      } else {
        snake.pop();
      }

      // Draw Screen
      ctx.fillStyle = '#0a0f1d';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Draw Food
      ctx.fillStyle = '#ffd700'; // Gold food
      ctx.fillRect(food.x * GRID_SIZE + 2, food.y * GRID_SIZE + 2, GRID_SIZE - 4, GRID_SIZE - 4);

      // Draw Snake
      snake.forEach((segment, idx) => {
        ctx.fillStyle = idx === 0 ? '#8da46e' : '#5a6d47';
        ctx.fillRect(segment.x * GRID_SIZE + 1, segment.y * GRID_SIZE + 1, GRID_SIZE - 2, GRID_SIZE - 2);
      });
    };

    const handleKeyDown = (e) => {
      switch (e.key) {
        case 'ArrowUp':
          if (dy !== 1) { dx = 0; dy = -1; }
          break;
        case 'ArrowDown':
          if (dy !== -1) { dx = 0; dy = 1; }
          break;
        case 'ArrowLeft':
          if (dx !== 1) { dx = -1; dy = 0; }
          break;
        case 'ArrowRight':
          if (dx !== -1) { dx = 1; dy = 0; }
          break;
        default:
          break;
      }
    };

    generateFood();
    window.addEventListener('keydown', handleKeyDown);
    gameInterval = setInterval(update, 120);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      clearInterval(gameInterval);
    };
  }, [gameOver]);

  const restartGame = () => {
    setScore(0);
    setGameOver(false);
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 999999,
        background: 'rgba(5, 8, 16, 0.85)', backdropFilter: 'blur(10px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px'
      }}
    >
      <div
        style={{
          background: '#0a0f1d',
          border: '2px solid #8da46e',
          borderRadius: '24px',
          padding: '30px',
          textAlign: 'center',
          boxShadow: '0 25px 70px rgba(0,0,0,0.7), 0 0 30px rgba(141,164,110,0.15)',
          maxWidth: '460px',
          width: '100%',
          color: '#f0f6fc',
          fontFamily: "'Courier New', Courier, monospace"
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px', fontSize: '0.9rem' }}>
          <span>Score: <strong style={{ color: '#ffd700' }}>{score}</strong></span>
          <span>High Score: <strong style={{ color: '#8da46e' }}>{highScore}</strong></span>
        </div>

        <canvas
          ref={canvasRef}
          width={400}
          height={400}
          style={{
            background: '#070a14',
            borderRadius: '12px',
            display: 'block',
            margin: '0 auto 20px',
            maxWidth: '100%',
            aspectRatio: '1/1'
          }}
        />

        {gameOver ? (
          <div>
            <h3 style={{ color: '#ff5f56', marginBottom: '15px' }}>GAME OVER</h3>
            <button
              onClick={restartGame}
              style={{
                background: '#ffd700', border: 'none', borderRadius: '8px',
                padding: '8px 20px', cursor: 'pointer', color: '#000',
                fontWeight: 'bold', marginRight: '10px'
              }}
            >
              Play Again
            </button>
            <button
              onClick={onClose}
              style={{
                background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: '8px', padding: '7px 20px', cursor: 'pointer', color: '#fff'
              }}
            >
              Exit
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.5)' }}>Use arrow keys to move</span>
            <button
              onClick={onClose}
              style={{
                background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.15)',
                borderRadius: '8px', padding: '6px 16px', cursor: 'pointer', color: '#fff', fontSize: '0.8rem'
              }}
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CareerPage;
